import React, { useState } from 'react';
import { Collapse, Descriptions, Modal, Tag, Tooltip } from 'antd';

import { ARROW_LEFT_RIGHT } from '../icons';
import { conversionRows, displayPrefsFor, familyLabel, isFamilyGrouped, splitConversionRows } from '../../utils/measurements';
import { formatMeasurement, getMeasure, isMultiMeasure, isOwnUnitId, measureOfUnit, ownUnitOf, readMeasurement, scopeOptions } from '../../utils/units';

/**
 * "What is this in ounces?" for one measurement value, wherever it is shown.
 *
 * A measurement displays in the unit its element's rules pick (a volume in
 * mL, a weight in lb-oz), which is the right default and the wrong answer
 * for the person who thinks in fl oz or decimal pounds today. Rather than
 * change the element or their preference, every rendered measurement carries
 * a small ⇄ affordance that opens this modal: the value converted to every
 * unit of its measure, the unit it was entered in and the one on screen
 * marked. Conversion is client-side from the stored base through the shared
 * module, so the numbers agree with every filter and export.
 *
 * The trigger and the modal are wrapped so neither a click nor a key press
 * inside them reaches the surface behind (a card tile or table row selects
 * on click; the modal renders in a portal but its React events still bubble
 * through this tree).
 *
 * COMMON FIRST. The modal leads with the handful of conversions someone
 * opened it for (splitConversionRows: inches → feet, feet and inches, cm;
 * lb-oz → lb, oz, kg) as one short list, the entered and shown rows at its
 * top. Every unit of the measure follows under "All units", collapsed, in
 * the grouping below; a measure the common list already covers (temperature,
 * fuel economy, a count) has no "All units" at all.
 *
 * GROUPING. A measure can split into FAMILIES — kinds of quantity spelling
 * that all convert but that nobody wants read back as one another. Four
 * measures split: volume (31 units across Liquid, Cubic, Dry (US) and
 * Imperial (UK)), energy (General / Electrical & heating), pressure
 * (General / Weather) and data size (Decimal / Bits / Binary). A flat wall
 * of 31 rows is not a glance, so a split measure groups by family FIRST
 * (collapsed, with only the entered and the shown family open) and by system
 * inside a family second. A measure with one family — mass, length,
 * temperature, the rest — and a short one whose families are for `auto`
 * alone (angle, frequency, fuel economy: isFamilyGrouped) render flat: the list,
 * or the Metric / Imperial / Other headings when both systems have more than
 * one unit to head.
 */

const SYSTEM_LABELS = { metric: 'Metric', imperial: 'Imperial', any: 'Other' };

const stop = event => event.stopPropagation();

// Metric / Imperial (/ Other) headings only when both systems have a few
// units to head (mass, length, area, pressure's General family, and volume's
// Liquid and Cubic families); a set with one or two per system (temperature,
// speed, volume's Dry and UK families, pressure's Weather) or none at all
// (duration, energy, data size) reads better as one flat list.
function systemSections(rows) {
	const bySystem = system => rows.filter(row => row.unit.system === system);
	if (!(bySystem('metric').length > 1 && bySystem('imperial').length > 1)) {
		return [{ key: 'all', title: null, rows }];
	}
	return ['metric', 'imperial', 'any']
		.map(system => ({ key: system, title: SYSTEM_LABELS[system], rows: bySystem(system) }))
		.filter(section => section.rows.length);
}

function familyGroups(rows) {
	const groups = [];
	rows.forEach(row => {
		let group = groups.find(g => g.key === row.family);
		if (!group) {
			group = { key: row.family, title: familyLabel(row.family), rows: [] };
			groups.push(group);
		}
		group.rows.push(row);
	});
	return groups;
}

function SectionList({ sections }) {
	return sections.map(section => (
		<Descriptions
			key={section.key}
			title={section.title}
			size='small'
			column={1}
			bordered
			className='measurement-conversions__group'
			items={section.rows.map(row => ({
				key: row.unit.id,
				label: <span className='measurement-conversions__unit'>{row.unit.label} <span className='measurement-conversions__symbol'>({row.unit.symbol})</span></span>,
				children: (
					<span className='measurement-conversions__row'>
						<span className='measurement-conversions__value'>{row.text}</span>
						{row.entered ? <Tag color='blue'>entered</Tag> : null}
						{row.shown && !row.entered ? <Tag>shown</Tag> : null}
					</span>
				),
			}))}
		/>
	));
}

const elementLabel = element => element?.label?.singular || element?.name || 'Measurement';

export function MeasurementConversionsModal({ open, onClose, element, value, unitSystem = 'default' }) {
	const elementOptions = element?.options || {};
	const prefs = displayPrefsFor(unitSystem);
	// An element that allows several measures (KPR-1085): the value converts,
	// groups and is named in its OWN measure — 250 g in kilograms and ounces,
	// headed "Mass" — as a one-measure element of that measure would.
	const read = isMultiMeasure(elementOptions) ? readMeasurement(value, elementOptions) : null;
	const options = (read && scopeOptions(elementOptions, measureOfUnit(read.unit), read.unit)) || elementOptions;
	const measure = getMeasure(options.measure);
	// An own unit (KPR-1109) converts to nothing: the modal shows the value
	// alone, headed by the unit's name.
	const own = isOwnUnitId(options.measure) ? ownUnitOf(options.measure, options) : null;
	const heading = measure?.label || (own ? `${own.plural.charAt(0).toUpperCase()}${own.plural.slice(1)}` : 'Value');
	const rows = conversionRows(value, options, prefs);
	const shown = formatMeasurement(value, options, prefs);

	const { common, rest } = splitConversionRows(rows);
	const families = familyGroups(rows);
	// Inside "All units", only the family the value was entered in and the
	// one it is shown in start open: the point of the modal is a glance, and
	// cubic yards are not what someone reading a recipe came for.
	const openKeys = [...new Set(rows.filter(row => row.entered || row.shown).map(row => row.family))];
	const allUnits = families.length > 1 && isFamilyGrouped(options.measure) ? (
		<Collapse
			className='measurement-conversions__families'
			defaultActiveKey={openKeys}
			items={families.map(family => ({
				key: family.key,
				label: family.title,
				children: <SectionList sections={systemSections(family.rows)} />,
			}))}
		/>
	) : (
		<SectionList sections={systemSections(rows)} />
	);
	// A unit with no common list (one the table has not met yet) has nothing
	// but its own row to lead with, so the full list starts open.
	const allOpen = common.length <= 1;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title={`${elementLabel(element)} conversions`}
			className='measurement-conversions'
		>
			{rows.length ? (
				<>
					<div className='measurement-conversions__lead'>
						<span className='micro-label'>{heading}</span>
						<span className='measurement-conversions__shown'>{shown}</span>
					</div>
					<SectionList sections={[{ key: 'common', title: null, rows: common }]} />
					{rest.length ? (
						<Collapse
							ghost
							className='measurement-conversions__all'
							defaultActiveKey={allOpen ? ['all'] : []}
							items={[{ key: 'all', label: `All units (${rows.length})`, children: allUnits }]}
						/>
					) : null}
				</>
			) : (
				<div className='measurement-conversions__empty'>This value cannot be converted.</div>
			)}
		</Modal>
	);
}

/**
 * The ⇄ trigger plus its modal. Renders nothing when the value is not a
 * readable measurement, so an empty field never grows an affordance.
 */
export function MeasurementConversionsButton({ element, value, unitSystem = 'default' }) {
	const [open, setOpen] = useState(false);
	const options = element?.options || {};
	if (!formatMeasurement(value, options, displayPrefsFor(unitSystem))) return null;
	const label = elementLabel(element);
	return (
		<span className='measurement-convert' onClick={stop} onKeyDown={stop} role='presentation'>
			<Tooltip title='See conversions'>
				<button
					type='button'
					className='measurement-convert__trigger'
					aria-label={`Convert ${label}`}
					aria-haspopup='dialog'
					aria-expanded={open}
					onClick={() => setOpen(true)}
				>
					<ARROW_LEFT_RIGHT />
				</button>
			</Tooltip>
			{open ? (
				<MeasurementConversionsModal
					open={open}
					onClose={() => setOpen(false)}
					element={element}
					value={value}
					unitSystem={unitSystem}
				/>
			) : null}
		</span>
	);
}

/**
 * A measurement as every read surface shows it: the element's formatted
 * text (its display rules, or the viewer's unit-system preference) followed
 * by the conversions trigger; the same em-dash every other empty value gets
 * when there is nothing to show. `conversions={false}` drops the trigger
 * where there is no room for it (the compact card tile).
 */
export default function MeasurementValue({ element, value, unitSystem = 'default', className = '', conversions = true }) {
	const text = formatMeasurement(value, element?.options || {}, displayPrefsFor(unitSystem));
	if (!text) return '—';
	return (
		<span className={`measurement-value ${className}`.trim()}>
			<span className='measurement-value__text'>{text}</span>
			{conversions ? <MeasurementConversionsButton element={element} value={value} unitSystem={unitSystem} /> : null}
		</span>
	);
}
